import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Api from '../Api';
import { Lessons } from '../Lessons';
import { LessonIcon } from '../components/LessonIcon';
import { ProgressIcon } from '../components/ProgressIcon';
import { TestHistoryView } from '../components/Test/TestHistoryView';
import { BlankTestView } from '../components/Test/BlankTestView';
import './Classroom.scss';

export const MAX_CLASS_SIZE = process.env.NODE_ENV === "production" ? 30 : 15;

export class Classroom extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      students: [],
      teacherDbId: null,
      loading: true,
      error: null,
      showAddForm: false,
      newStudentName: '',
      addError: '',
      addLoading: false,
      selectedStudent: null,
      testHistoryMap: {},
      testHistoryLoading: false,
      viewingHistory: null,
      printingBlankTest: null,
      editingStudentId: null,
      editName: '',
      editError: '',
      editLoading: false,
    };
  }

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.viewingHistory !== this.state.viewingHistory) {
      document.body.classList.toggle('cr-viewing-history', !!this.state.viewingHistory);
    }
    if (prevState.printingBlankTest !== this.state.printingBlankTest) {
      document.body.classList.toggle('cr-printing-blank', !!this.state.printingBlankTest);
    }
  }

  componentWillUnmount() {
    document.body.classList.remove('cr-viewing-history');
    document.body.classList.remove('cr-printing-blank');
  }

  loadData = async () => {
    try {
      this.setState({ loading: true, error: null });
      const [students, teacher] = await Promise.all([
        Api.getStudentsByTeacher(this.props.educatorEmail),
        Api.getTeacherByEmail(this.props.educatorEmail),
      ]);
      this.setState({ students, teacherDbId: teacher.teacherId, loading: false });
    } catch {
      this.setState({ loading: false, error: 'Failed to load classroom data.' });
    }
  }

  lessonProgressStatuses = (progressJson) => {
    let progressMap = {};
    try {
      const progress = JSON.parse(progressJson);
      progress.forEach(p => { progressMap[p.section] = p.status; });
    } catch { /* fall through with empty map */ }

    return Lessons.lessons.map(lesson => {
      const statuses = lesson.sections.map(s => progressMap[s.id] || '');
      if (statuses.every(s => s === 'completed')) return 'completed';
      if (statuses.some(s => s === 'completed' || s === 'viewed')) return 'viewed';
      return '';
    });
  }

  onDeleteStudent = async (studentId, studentName) => {
    if (!window.confirm(`Delete ${studentName}? This cannot be undone.`)) return;
    try {
      await Api.deleteStudent(studentId);
      this.setState(prev => ({
        students: prev.students.filter(s => s.studentId !== studentId)
      }));
    } catch {
      this.setState({ error: 'Failed to delete student.' });
    }
  }

  onShowAddForm = () => {
    this.setState({ showAddForm: true, newStudentName: '', addError: '' });
  }

  onCancelAdd = () => {
    this.setState({ showAddForm: false, newStudentName: '', addError: '' });
  }

  onChangeNewStudentName = (e) => {
    this.setState({ newStudentName: e.target.value });
  }

  onAddStudent = async (e) => {
    e.preventDefault();
    const name = this.state.newStudentName.trim();
    if (!name) {
      this.setState({ addError: "Enter the student's name" });
      return;
    }
    const duplicate = this.state.students.some(
      s => s.name.toLowerCase() === name.toLowerCase()
    );
    if (duplicate) {
      this.setState({ addError: 'A student with that name already exists.' });
      return;
    }
    this.setState({ addLoading: true, addError: '' });
    try {
      const student = await Api.addStudent(this.state.teacherDbId, name);
      this.setState(prev => ({
        students: [...prev.students, student],
        showAddForm: false,
        newStudentName: '',
        addLoading: false,
      }));
    } catch (err) {
      const msg = err.message === 'duplicate'
        ? 'A student with that name already exists.'
        : 'Failed to add student.';
      this.setState({ addLoading: false, addError: msg });
    }
  }

  onStartEdit = (student) => {
    this.setState({
      editingStudentId: student.studentId,
      editName: student.name,
      editError: '',
    });
  }

  onCancelEdit = () => {
    this.setState({ editingStudentId: null, editName: '', editError: '' });
  }

  onChangeEditName = (e) => {
    this.setState({ editName: e.target.value });
  }

  onSaveEdit = async (studentId) => {
    const name = this.state.editName.trim();
    if (!name) {
      this.setState({ editError: "Enter the student's name" });
      return;
    }
    const duplicate = this.state.students.some(
      s => s.studentId !== studentId && s.name.toLowerCase() === name.toLowerCase()
    );
    if (duplicate) {
      this.setState({ editError: 'A student with that name already exists.' });
      return;
    }
    this.setState({ editLoading: true, editError: '' });
    try {
      await Api.renameStudent(studentId, name);
      this.setState(prev => ({
        students: prev.students.map(s =>
          s.studentId === studentId ? { ...s, name } : s
        ),
        editingStudentId: null,
        editName: '',
        editLoading: false,
      }));
    } catch (err) {
      const msg = err.message === 'duplicate'
        ? 'A student with that name already exists.'
        : 'Failed to rename student.';
      this.setState({ editLoading: false, editError: msg });
    }
  }

  onProgressClick = async (student) => {
    this.setState({ selectedStudent: student, testHistoryMap: {}, testHistoryLoading: true, viewingHistory: null });
    try {
      const records = await Api.getTestHistoryForStudent(student.studentId);
      const map = {};
      records.forEach(r => {
        if (!map[r.testName]) map[r.testName] = [];
        map[r.testName].push(r);
      });
      this.setState({ testHistoryMap: map, testHistoryLoading: false });
    } catch {
      this.setState({ testHistoryLoading: false });
    }
  }

  onCloseReport = () => {
    this.setState({ selectedStudent: null, testHistoryMap: {}, viewingHistory: null });
  }

  onViewHistory = (sectionId) => {
    const records = this.state.testHistoryMap[sectionId];
    if (!records || records.length === 0) return;
    this.setState({ viewingHistory: { testName: sectionId, record: records[0] } });
  }

  onCloseHistory = () => {
    this.setState({ viewingHistory: null });
  }

  onPrintBlankTest = (sectionId) => {
    this.setState({ printingBlankTest: sectionId });
  }

  onClosePrintBlankTest = () => {
    this.setState({ printingBlankTest: null });
  }

  onClearTestResult = async (sectionId, sectionName) => {
    if (!window.confirm(`Clear results for "${sectionName}"? This cannot be undone.`)) return;
    const { selectedStudent } = this.state;
    let progress;
    try { progress = JSON.parse(selectedStudent.progress); } catch { progress = []; }
    const updated = progress.map(p =>
      p.section === sectionId
        ? { ...p, status: '', completedOn: null, testResults: p.testResults ? { correct: null, score: null, time: null } : null }
        : p
    );
    const updatedJson = JSON.stringify(updated);
    await Api.saveProgress(selectedStudent.studentId, updated);
    const updatedStudent = { ...selectedStudent, progress: updatedJson };
    this.setState(prev => ({
      selectedStudent: updatedStudent,
      students: prev.students.map(s =>
        s.studentId === selectedStudent.studentId ? updatedStudent : s
      ),
    }));
  }

  onPrintReport = (studentName) => {
    const currentTitle = document.title;
    document.title = `Progress Report - ${studentName}`;
    window.print();
    document.title = currentTitle;
  }

  formatDate = (completedOn) => {
    if (!completedOn) return '';
    const d = new Date(completedOn);
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  }

  formatScore = (testResults) => {
    if (testResults && testResults.score !== null)
      return Math.round(testResults.score * 100) + '%';
    return '';
  }

  formatTime = (testResults) => {
    if (testResults && testResults.time)
      return `${Math.floor(testResults.time / 60)}:${String(testResults.time % 60).padStart(2, '0')}`;
    return '';
  }

  renderProgressModal = () => {
    const { selectedStudent } = this.state;
    let progress;
    try {
      progress = JSON.parse(selectedStudent.progress);
    } catch {
      progress = [];
    }
    const progressMap = {};
    progress.forEach(p => { progressMap[p.section] = p; });

    return (
      <div className="cr-modal-overlay" onClick={this.onCloseReport}>
        <div className="cr-modal" onClick={e => e.stopPropagation()}>
          <div className="cr-modal-header">
            <span className="cr-modal-title">Progress Report: {selectedStudent.name}</span>
            <div className="cr-modal-header-actions">
              <button className="cr-print-button" onClick={() => this.onPrintReport(selectedStudent.name)}>
                Print
              </button>
              <button className="cr-modal-close" onClick={this.onCloseReport}>&times;</button>
            </div>
          </div>
          <div className="cr-modal-body">
            <div className="cr-report-cols">
              <div className="cr-col-status" />
              <div className="cr-col-type">Type</div>
              <div className="cr-col-sect">Sect</div>
              <div className="cr-col-name">Lesson Description</div>
              <div className="cr-col-facts">Facts</div>
              <div className="cr-col-date">Date</div>
              <div className="cr-col-correct">Correct</div>
              <div className="cr-col-score">Score</div>
              <div className="cr-col-time">Time</div>
              <div className="cr-col-view" />
              <div className="cr-col-clear" />
              <div className="cr-col-print" />
            </div>
            {Lessons.lessons.map(lesson => (
              <div key={lesson.id} className="cr-report-lesson">
                <div className="cr-report-lesson-name">{lesson.name}</div>
                {lesson.sections.map(section => {
                  const result = progressMap[section.id] || { status: '', completedOn: null, testResults: null };
                  const isTest = section.type === 'test';
                  return (
                    <div key={section.id} className="cr-report-row">
                      <div className="cr-col-status"><ProgressIcon status={result.status} /></div>
                      <div className="cr-col-type"><LessonIcon lessonType={section.type} /></div>
                      <div className="cr-col-sect">{section.id}</div>
                      <div className="cr-col-name cr-col-name-left">{section.name}</div>
                      <div className="cr-col-facts">{section.facts}</div>
                      <div className="cr-col-date">{this.formatDate(result.completedOn)}</div>
                      {isTest ? (
                        <>
                          <div className="cr-col-correct">{result.testResults && result.testResults.correct}</div>
                          <div className="cr-col-score">{this.formatScore(result.testResults)}</div>
                          <div className="cr-col-time">{this.formatTime(result.testResults)}</div>
                        </>
                      ) : (
                        <div className="cr-col-filler" />
                      )}
                      <div className="cr-col-view">
                        {isTest && this.state.testHistoryMap[section.id]?.length > 0 && (
                          <button
                            className="cr-view-btn"
                            onClick={() => this.onViewHistory(section.id)}
                          >
                            View
                          </button>
                        )}
                      </div>
                      <div className="cr-col-clear">
                        {result.status !== '' && (
                          <button
                            className="cr-clear-btn"
                            onClick={() => this.onClearTestResult(section.id, section.name)}
                          >
                            clear
                          </button>
                        )}
                      </div>
                      <div className="cr-col-print">
                        {isTest && (
                          <button
                            className="cr-print-blank-btn"
                            onClick={() => this.onPrintBlankTest(section.id)}
                          >
                            print
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { educatorEmail, subscriptionStatus, onTeacherInstruction } = this.props;
    const {
      students, loading, error,
      showAddForm, newStudentName, addError, addLoading,
      selectedStudent, viewingHistory, printingBlankTest,
      editingStudentId, editName, editError, editLoading,
    } = this.state;
    const atCapacity = students.length >= MAX_CLASS_SIZE;
    const isBusy = showAddForm || editingStudentId !== null;

    return (
      <div className="classroom-page">
        {selectedStudent && (
          <div className="cr-print-header">
            <div className="cr-print-student">{selectedStudent.name}</div>
            <div className="cr-print-teacher">{educatorEmail}</div>
          </div>
        )}
        {selectedStudent && this.renderProgressModal()}
        {viewingHistory && (
          <TestHistoryView
            testName={viewingHistory.testName}
            historyRecord={viewingHistory.record}
            onClose={this.onCloseHistory}
          />
        )}
        {printingBlankTest && (
          <BlankTestView
            testName={printingBlankTest}
            onClose={this.onClosePrintBlankTest}
          />
        )}

        <div className="classroom-header">
          <div className="classroom-educator-info">
            <span className="classroom-email">{educatorEmail}</span>
            <span className="classroom-status">{subscriptionStatus}</span>
          </div>
          <button className="classroom-directions-button" onClick={onTeacherInstruction}>
            Teacher Instructions
          </button>
          <div className="classroom-header-right">
            <button
              className="classroom-add-button"
              onClick={this.onShowAddForm}
              disabled={atCapacity || isBusy}
              title={atCapacity ? `Class is at the maximum size of ${MAX_CLASS_SIZE} students` : 'Add a new student'}
            >
              + Add Student
            </button>
          </div>
        </div>

        {showAddForm && (
          <form className="classroom-add-form" onSubmit={this.onAddStudent}>
            <input
              className="classroom-add-input"
              type="text"
              autoFocus
              autoComplete="off"
              placeholder="Student's full name"
              value={newStudentName}
              onChange={this.onChangeNewStudentName}
            />
            <input
              className="classroom-add-submit"
              type="submit"
              value={addLoading ? 'Adding…' : 'Add'}
              disabled={addLoading}
            />
            <button className="classroom-add-cancel" type="button" onClick={this.onCancelAdd}>
              Cancel
            </button>
            {addError && <span className="classroom-add-error">{addError}</span>}
          </form>
        )}

        {error && <div className="classroom-error">{error}</div>}

        {loading && <div className="classroom-loading">Loading students…</div>}

        {!loading && !error && students.length === 0 && (
          <div className="classroom-empty">No students yet. Use &ldquo;Add Student&rdquo; above to get started.</div>
        )}

        {!loading && students.length > 0 && (
          <div className="classroom-table-container">
          <table className="classroom-table">
            <thead>
              <tr>
                <th className="classroom-th classroom-th-student">Student</th>
                <th className="classroom-th classroom-th-progress">Progress</th>
                <th className="classroom-th classroom-th-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {students.map(student => {
                const isEditing = editingStudentId === student.studentId;
                return (
                  <tr key={student.studentId} className="classroom-tr">
                    <td
                      className={`classroom-td classroom-td-name${isEditing ? ' classroom-td-name-editing' : ''}`}
                      onClick={!isEditing && !isBusy ? () => this.onStartEdit(student) : undefined}
                      title={!isEditing && !isBusy ? 'Click to rename' : undefined}
                    >
                      {isEditing ? (
                        <div className="classroom-rename-cell">
                          <div className="classroom-rename-row">
                            <input
                              className="classroom-rename-input"
                              type="text"
                              autoFocus
                              autoComplete="off"
                              value={editName}
                              onChange={this.onChangeEditName}
                              onKeyDown={e => {
                                if (e.key === 'Enter') this.onSaveEdit(student.studentId);
                                if (e.key === 'Escape') this.onCancelEdit();
                              }}
                            />
                            <button
                              className="classroom-rename-confirm"
                              onClick={() => this.onSaveEdit(student.studentId)}
                              disabled={editLoading}
                              title="Save"
                            >
                              <FontAwesomeIcon icon="check-circle" />
                            </button>
                            <button
                              className="classroom-rename-cancel"
                              onClick={this.onCancelEdit}
                              disabled={editLoading}
                              title="Cancel"
                            >
                              <FontAwesomeIcon icon="times" />
                            </button>
                          </div>
                          {editError && <span className="classroom-rename-error">{editError}</span>}
                        </div>
                      ) : (
                        student.name
                      )}
                    </td>
                    <td
                      className="classroom-td classroom-td-progress"
                      onClick={!isEditing ? () => this.onProgressClick(student) : undefined}
                    >
                      <div className="classroom-progress-line">
                        {this.lessonProgressStatuses(student.progress).map((status, i) => (
                          <div key={i} className="classroom-progress-item">
                            <span className="classroom-progress-num">{i + 1}</span>
                            <ProgressIcon status={status} className="classroom-progress-icon" />
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="classroom-td classroom-td-actions">
                      <button
                        className="classroom-progress-button"
                        onClick={() => this.onProgressClick(student)}
                        disabled={isEditing}
                      >
                        Details
                      </button>
                      <button
                        className="classroom-delete-button"
                        onClick={() => this.onDeleteStudent(student.studentId, student.name)}
                        disabled={isEditing}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}

        {atCapacity && (
          <div className="classroom-capacity-msg">
            Class is at maximum capacity ({MAX_CLASS_SIZE} students).
          </div>
        )}

      </div>
    );
  }
}
